import {
  type ColumnDef,
  type OnChangeFn,
  type SortingState
} from '@tanstack/react-table';
import {
  CheckIcon,
  FolderOpenIcon,
  HourglassIcon,
  MoveDiagonalIcon,
  XIcon
} from 'lucide-react';
import { type ReactNode, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router';

import { PaginationControls } from './PaginationControls';
import type { GetDealOpportunitiesData, OpportunityNode } from './types';
import { formatCurrencyValue } from './utils';

import { TruncatedTextTooltip } from '@/components/TruncatedTextTooltip';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DataTable } from '@/components/ui/data-table';
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle
} from '@/components/ui/empty';
import { Spinner } from '@/components/ui/spinner';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from '@/components/ui/tooltip';
import { getInitials } from '@/utils/getInitials';

interface TruncatedBadgeProps {
  children: React.ReactNode;
  tooltipContent: string;
  variant?: 'pastel-slate';
}

const TruncatedBadge = ({
  children,
  tooltipContent,
  variant
}: TruncatedBadgeProps) => {
  const [isTruncated, setIsTruncated] = useState(false);
  const textRef = useRef<HTMLSpanElement>(null);

  const handleMouseEnter = () => {
    if (textRef.current) {
      setIsTruncated(textRef.current.scrollWidth > textRef.current.clientWidth);
    }
  };

  return (
    <Tooltip open={isTruncated ? undefined : false}>
      <TooltipTrigger
        asChild
        onMouseEnter={handleMouseEnter}
      >
        <Badge
          className="max-w-full justify-start overflow-hidden"
          variant={variant}
        >
          <span
            className="block max-w-full min-w-0 truncate"
            ref={textRef}
          >
            {children}
          </span>
        </Badge>
      </TooltipTrigger>
      <TooltipContent className="max-w-md">{tooltipContent}</TooltipContent>
    </Tooltip>
  );
};

interface InactiveTabProps {
  currentPage: number;
  data1?: GetDealOpportunitiesData;
  loading1: boolean;
  newDealTrigger: ReactNode;
  onPageChange: (page: number) => void;
  onSortingChange: OnChangeFn<SortingState>;
  sorting: SortingState;
}

const ITEMS_PER_PAGE = 20;

export const InactiveTab = ({
  currentPage,
  data1,
  loading1,
  newDealTrigger,
  onPageChange,
  onSortingChange,
  sorting
}: InactiveTabProps) => {
  const deals = data1?.opportunities.edges ?? [];
  const inactiveDeals = deals
    .filter(edge => edge.node.stage === 'inactive')
    .map(edge => edge.node);

  // Get total count from the query response
  const totalDeals = data1?.opportunities.totalCount ?? 0;
  const totalPages = Math.ceil(totalDeals / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = Math.min(startIndex + ITEMS_PER_PAGE, totalDeals);

  const getPageNumbers = (): ('ellipsis' | number)[] => {
    const pages: ('ellipsis' | number)[] = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);

      if (currentPage > 3) {
        pages.push('ellipsis');
      }

      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      if (currentPage < totalPages - 2) {
        pages.push('ellipsis');
      }

      if (totalPages > 1) {
        pages.push(totalPages);
      }
    }

    return pages;
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      onPageChange(currentPage + 1);
    }
  };

  const handlePageClick = (page: number) => {
    onPageChange(page);
  };

  const columns = useMemo<ColumnDef<OpportunityNode>[]>(
    () => [
      {
        accessorKey: 'targetEntityName',
        cell: ({ row }) => (
          <div className="font-medium">
            <TruncatedTextTooltip>
              {row.original.targetEntityName || '-'}
            </TruncatedTextTooltip>
          </div>
        ),
        enableSorting: false,
        header: () => (
          <div className="w-[180px] text-xs font-semibold">Company Name</div>
        ),
        meta: {
          cellClassName: 'w-[180px] max-w-[180px]',
          headerClassName: 'w-[180px] max-w-[180px]'
        }
      },
      {
        accessorKey: 'projectName',
        cell: ({ row }) => (
          <TruncatedTextTooltip>
            {row.original.projectName || '-'}
          </TruncatedTextTooltip>
        ),
        header: () => (
          <div className="w-[140px] text-xs font-semibold">Project Name</div>
        )
      },
      {
        accessorKey: 'dealSource',
        cell: ({ row }) =>
          row.original.dealSource ? (
            <div className="flex items-center gap-x-2 overflow-hidden">
              <Avatar className="shrink-0">
                <AvatarFallback>
                  {getInitials(row.original.dealSource.name)}
                </AvatarFallback>
              </Avatar>
              <TruncatedTextTooltip>
                {row.original.dealSource.name}
              </TruncatedTextTooltip>
            </div>
          ) : (
            '-'
          ),
        enableSorting: false,
        header: () => (
          <div className="w-[150px] text-xs font-semibold">
            Deal Source Firm
          </div>
        )
      },
      {
        accessorKey: 'mdTeamId',
        cell: ({ row }) =>
          row.original.mdTeamId ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="*:data-[slot=avatar]:ring-background flex -space-x-2 *:data-[slot=avatar]:ring-2">
                  <Avatar key={row.original.mdTeamId.id}>
                    <AvatarImage
                      alt="@shadcn"
                      src={row.original.mdTeamId.name}
                    />
                    <AvatarFallback>
                      {row.original.mdTeamId.name.split(' ')[0].charAt(0)}
                    </AvatarFallback>
                  </Avatar>
                </div>
              </TooltipTrigger>
              <TooltipContent className="max-w-md">
                {row.original.mdTeamId.name}
              </TooltipContent>
            </Tooltip>
          ) : (
            '-'
          ),
        enableSorting: false,
        header: () => (
          <div className="w-[70px] text-xs font-semibold">Deal Lead</div>
        )
      },
      {
        accessorKey: 'readableSmcIndustry',
        cell: ({ row }) =>
          row.original.readableSmcIndustry ? (
            <TruncatedBadge
              tooltipContent={row.original.readableSmcIndustry}
              variant="pastel-slate"
            >
              {row.original.readableSmcIndustry}
            </TruncatedBadge>
          ) : (
            '-'
          ),
        enableSorting: false,
        header: () => (
          <div className="w-[120px] text-xs font-semibold">Industry</div>
        )
      },
      {
        accessorKey: 'status',
        cell: ({ row }) => (
          <>
            {row.original.status === 'closed' && (
              <Badge variant="green">
                <CheckIcon />
                Closed
              </Badge>
            )}
            {row.original.status === 'killed' && (
              <Badge variant="red">
                <XIcon />
                Killed
              </Badge>
            )}
            {row.original.status === 'long_term' && (
              <Badge variant="pastel-slate">
                <HourglassIcon />
                Long Term
              </Badge>
            )}
            {!['closed', 'killed', 'long_term'].includes(row.original.status) &&
              '-'}
          </>
        ),
        header: () => (
          <div className="w-[100px] text-xs font-semibold">Status</div>
        )
      },
      {
        accessorKey: 'createDate',
        cell: ({ row }) => (
          <TruncatedTextTooltip>
            {row.original.createDate
              ? row.original.createDate.slice(0, 10)
              : '-'}
          </TruncatedTextTooltip>
        ),
        header: () => <div className="text-xs font-semibold">Created on</div>,
        meta: {
          cellClassName: 'w-[110px] max-w-[110px]',
          headerClassName: 'w-[110px] max-w-[110px]'
        }
      },
      {
        accessorKey: 'totalProposedDebtInvestment',
        cell: ({ row }) => (
          <div className="text-right">
            {row.original.totalProposedDebtInvestment != null ? (
              <TruncatedTextTooltip>
                {formatCurrencyValue(row.original.totalProposedDebtInvestment)}
              </TruncatedTextTooltip>
            ) : (
              '-'
            )}
          </div>
        ),
        header: () => (
          <div className="truncate text-right text-xs font-semibold whitespace-nowrap">
            Proposed Debt Investment
          </div>
        ),
        meta: {
          cellClassName: 'w-[170px] max-w-[170px]',
          headerClassName: 'w-[170px] max-w-[170px]'
        }
      },
      {
        accessorKey: 'totalProposedEquityInvestment',
        cell: ({ row }) => (
          <div className="text-right">
            {row.original.totalProposedEquityInvestment != null ? (
              <TruncatedTextTooltip>
                {formatCurrencyValue(
                  row.original.totalProposedEquityInvestment
                )}
              </TruncatedTextTooltip>
            ) : (
              '-'
            )}
          </div>
        ),
        header: () => (
          <div className="truncate text-right text-xs font-semibold whitespace-nowrap">
            Proposed Equity Investment
          </div>
        ),
        meta: {
          cellClassName: 'w-[180px] max-w-[180px]',
          headerClassName: 'w-[180px] max-w-[180px]'
        }
      },
      {
        accessorKey: 'ltmEbitda',
        cell: ({ row }) => (
          <div className="text-right">
            {row.original.ltmEbitda != null ? (
              <TruncatedTextTooltip>
                {formatCurrencyValue(row.original.ltmEbitda)}
              </TruncatedTextTooltip>
            ) : (
              '-'
            )}
          </div>
        ),
        header: () => (
          <div className="text-right text-xs font-semibold">EBITDA</div>
        ),
        meta: {
          cellClassName: 'w-[100px] max-w-[100px]',
          headerClassName: 'w-[100px] max-w-[100px]'
        }
      },
      {
        accessorKey: 'referencePoint',
        cell: ({ row }) => (
          <TruncatedTextTooltip>
            {row.original.referencePoint || '-'}
          </TruncatedTextTooltip>
        ),
        header: () => (
          <div className="w-[100px] text-xs font-semibold">Closed Date</div>
        )
      },
      {
        accessorKey: 'killedDate',
        cell: ({ row }) => (
          <TruncatedTextTooltip>
            {row.original.killedDate || '-'}
          </TruncatedTextTooltip>
        ),
        header: () => (
          <div className="w-[100px] text-xs font-semibold">Killed Date</div>
        )
      },
      {
        cell: ({ row }) => (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                asChild
                size="icon"
                variant="ghost"
              >
                <Link to={`/investments/direct/${row.original.id}/overview`}>
                  <MoveDiagonalIcon />
                </Link>
              </Button>
            </TooltipTrigger>
            <TooltipContent className="max-w-md">Deal Details</TooltipContent>
          </Tooltip>
        ),
        enableSorting: false,
        id: 'actions',
        size: 52
      }
    ],
    []
  );

  return (
    <div className="grid h-full max-h-full grid-rows-[1fr_auto]">
      {loading1 || !data1?.opportunities ? (
        <div className="flex min-h-[60vh] w-full items-center justify-center">
          <Spinner className="size-8 text-blue-500" />
        </div>
      ) : inactiveDeals.length === 0 ? (
        <Empty className="pt-52!">
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <FolderOpenIcon />
            </EmptyMedia>
            <EmptyTitle>No deals yet</EmptyTitle>
            <EmptyDescription>
              You haven't added any deal yet. Get started by adding your first
              deal.
            </EmptyDescription>
          </EmptyHeader>
          <EmptyContent>{newDealTrigger}</EmptyContent>
        </Empty>
      ) : (
        <DataTable
          className="min-w-max table-fixed"
          columns={columns}
          data={inactiveDeals}
          enableColumnResizing
          getRowId={row => row.id}
          initialColumnPinning={{
            left: ['actions', 'targetEntityName']
          }}
          manualSorting
          onSortingChange={onSortingChange}
          persistColumnSizingKey="direct-underwriting-inactive-table"
          sorting={sorting}
        />
      )}

      <PaginationControls
        currentPage={currentPage}
        endIndex={endIndex}
        getPageNumbers={getPageNumbers}
        itemLabel="deals"
        itemsPerPage={ITEMS_PER_PAGE}
        onNextPage={handleNextPage}
        onPageClick={handlePageClick}
        onPreviousPage={handlePreviousPage}
        startIndex={startIndex}
        totalItems={totalDeals}
        totalPages={totalPages}
      />
    </div>
  );
};
